import { MB_Button } from '@/components/general/button';
import { MB_Pressable } from '@/components/general/pressable';
import { isMobile } from '@/utils/general/device-info';
import React, { useState } from 'react';
import { StyleProp, StyleSheet, TextStyle, View, ViewStyle } from 'react-native';
import Tooltip, { TooltipChildrenContext } from 'react-native-walkthrough-tooltip';
import { COLORS } from './constants';

interface MB_ToolTipProps extends CustomToolTipElementProps {
  /**
   * If set to true, the tooltip will be always visible.
   */
  overrideIsVisible?: boolean;

  /**
   * The tooltip's children elements.
   */
  children?: React.ReactNode;

  /**
   * Style of the content.
   */
  toolTipElementWrapperStyle?: StyleProp<ViewStyle>;

  /**
   * If true, the tooltip will not appear elevated.
   *
   * Default: true.
   */
  disableShadow?: boolean;

  /**
   * Where to position the tooltip.
   *
   * Default: 'top'.
   */
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';

  /**
   * The distance between the tooltip-rendered content and the
   * arrow pointing to it.
   *
   * Default: 4.
   */
  childContentSpacing?: number;

  /**
   * Style of the children wrapper.
   */
  childWrapperStyle?: StyleProp<ViewStyle>;

  /**
   * Style of the tooltip wrapper.
   */
  wrapperStyle?: StyleProp<ViewStyle>;

  /**
   * Color of the fullscreen background beneath the tooltip.
   */
  backgroundColor?: string;

  /**
   * Show the tooltip on click instead of hover.
   */
  activateWithPress?: boolean;

  /**
   * By default, the user can interact with the children of the tooltip.
   * When set to false, the user can't interact with the children while the
   * tooltip is visible.
   */
  allowChildInteraction?: boolean;

  /**
   * By default, `onClose` is called when the user interacts with the
   * content of the tooltip. When set to false, the tooltip will not close
   * when the user interacts with the content.
   */
  closeOnContentInteraction?: boolean;

  /**
   * By default, `onClose` is called when the user interacts with the
   * children of the tooltip. When set to false, the tooltip will not close
   * when the user interacts with the child element.
   */
  closeOnChildInteraction?: boolean;

  /**
   * Disabled the tooltip from closing when the user interacts with any
   * element.
   */
  disableCloseOnAnyInteraction?: boolean;
}

interface CustomToolTipElementProps {
  /**
   * The tooltip content.
   */
  toolTipElement: React.ReactElement;

  /**
   * The title of the left button.
   *
   * Default: 'Skip'.
   */
  leftButtonTitle?: string;

  /**
   * The style of the left button.
   */
  leftButtonStyle?: StyleProp<ViewStyle>;

  /**
   * The style of the left button's text.
   */
  leftButtonTextStyle?: StyleProp<TextStyle>;

  /**
   * Callback for when the left button is pressed.
   */
  onLeftButtonPressed?: () => void;

  /**
   * The title of the right button.
   *
   * Default: 'Got It'.
   */
  rightButtonTitle?: string;

  /**
   * The style of the right button.
   */
  rightButtonStyle?: StyleProp<ViewStyle>;

  /**
   * The style of the right button's text.
   */
  rightButtonTextStyle?: StyleProp<TextStyle>;

  /**
   * Callback for when the right button is pressed.
   */
  onRightButtonPressed?: () => void;
}

// TODO: I use forwardRef here becuase there is an issue with library and it throws a warning if we don`t use forwardRef.
const Content = React.forwardRef<undefined, CustomToolTipElementProps>((props, _ref) => {
  return (
    <>
      {props.toolTipElement}
      {(props.onLeftButtonPressed !== undefined || props.onRightButtonPressed !== undefined) && (
        <View style={[styles.buttonsWrapper, !props.onLeftButtonPressed && { justifyContent: 'flex-end' }]}>
          {props.onLeftButtonPressed && (
            <MB_Button
              title={props.leftButtonTitle ?? 'Skip'}
              onPress={props.onLeftButtonPressed}
              style={[{ height: '100%', backgroundColor: 'transparent' }, props.leftButtonStyle]}
              textStyle={[styles.leftButtonTextStyle, props.leftButtonTextStyle]}
            />
          )}

          {props.onRightButtonPressed && (
            <MB_Button
              title={props.rightButtonTitle ?? 'Got It'}
              onPress={props.onRightButtonPressed}
              style={[styles.rightButtonStyle, props.rightButtonStyle]}
              textStyle={[baseStyles.buttonTextStyle, props.rightButtonTextStyle]}
            />
          )}
        </View>
      )}
    </>
  );
});

const MB_ToolTipHover = ({
  overrideIsVisible,
  children,
  toolTipElementWrapperStyle,
  disableShadow = true,
  placement = 'top',
  childContentSpacing = 4,
  childWrapperStyle,
  wrapperStyle,
  backgroundColor,
  activateWithPress,
  allowChildInteraction,
  closeOnContentInteraction,
  closeOnChildInteraction,
  disableCloseOnAnyInteraction,
  toolTipElement,
  onLeftButtonPressed,
  onRightButtonPressed,
  leftButtonTitle,
  leftButtonStyle,
  leftButtonTextStyle,
  rightButtonTitle,
  rightButtonStyle,
  rightButtonTextStyle,
}: MB_ToolTipProps) => {
  const [isToolTipVisible, setIsToolTipVisible] = useState(false);

  return (
    <Tooltip
      isVisible={overrideIsVisible ?? isToolTipVisible}
      content={
        <Content
          toolTipElement={toolTipElement}
          onLeftButtonPressed={onLeftButtonPressed}
          onRightButtonPressed={onRightButtonPressed}
          leftButtonTitle={leftButtonTitle}
          leftButtonStyle={leftButtonStyle}
          leftButtonTextStyle={leftButtonTextStyle}
          rightButtonTitle={rightButtonTitle}
          rightButtonStyle={rightButtonStyle}
          rightButtonTextStyle={rightButtonTextStyle}
        />
      }
      // TODO: There are some issues with some of these props, just wait for update.
      allowChildInteraction={allowChildInteraction}
      closeOnChildInteraction={closeOnChildInteraction}
      closeOnContentInteraction={closeOnContentInteraction}
      placement={placement}
      childContentSpacing={childContentSpacing}
      displayInsets={{ left: 0, top: 0, right: 0, bottom: 0 }}
      disableShadow={disableShadow}
      contentStyle={[styles.toolTipElementWrapperStyle, toolTipElementWrapperStyle]}
      onClose={() => {
        // TODO: Remove this once they merge that branch that allows disable interactions on background press.
        if (disableCloseOnAnyInteraction) {
          return;
        }
        setIsToolTipVisible(false);
      }}
      backgroundColor={backgroundColor}
      parentWrapperStyle={[styles.parentWrapperStyle, wrapperStyle]}
    >
      <TooltipChildrenContext.Consumer>
        {({ tooltipDuplicate }) => {
          return (
            <MB_Pressable
              style={childWrapperStyle}
              onPress={() => {
                if (activateWithPress ?? isMobile) {
                  setIsToolTipVisible(true);
                }
              }}
              onHoverIn={() => {
                if (!(activateWithPress ?? isMobile) && !tooltipDuplicate) {
                  setIsToolTipVisible(true);
                }
              }}
              onHoverOut={() => {
                if (!(activateWithPress ?? isMobile) && tooltipDuplicate) {
                  setIsToolTipVisible(false);
                }
              }}
            >
              {children}
            </MB_Pressable>
          );
        }}
      </TooltipChildrenContext.Consumer>
    </Tooltip>
  );
};

export { MB_ToolTipHover };

const baseStyles = StyleSheet.create({
  buttonTextStyle: {
    fontSize: 14,
    fontWeight: '500',
    color: COLORS.white,
    textAlign: 'center',
  },
});

const styles = StyleSheet.create({
  parentWrapperStyle: {
    alignSelf: 'flex-start',
  },
  toolTipElementWrapperStyle: {
    alignItems: 'center',
  },
  buttonsWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignSelf: 'stretch',
    height: 24,
    marginTop: 18,
  },
  rightButtonStyle: {
    height: '100%',
    backgroundColor: COLORS.button,
  },
  leftButtonStyle: {
    height: '100%',
    backgroundColor: 'transparent',
  },
  leftButtonTextStyle: StyleSheet.flatten([
    baseStyles.buttonTextStyle,
    {
      color: COLORS.button,
    },
  ]),
});
